import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Lock, Mail, LogIn, Loader2 } from 'lucide-react';
import Logo from './Logo';
import { toast } from '../lib/toast';
import { send2FACode } from '../lib/email';
import { User } from '@supabase/supabase-js';
import { useDarkMode } from '../hooks/useDarkMode';
import { isNetworkError } from '../lib/errorHandling';
import { sendPasswordResetEmail } from '../lib/email';

// Validation helpers
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidPassword = (password: string): boolean => {
  return password.length >= 6;
};

export default function Auth() {
  const [loading, setLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [pendingUser, setPendingUser] = useState<User | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [oauthSettings, setOauthSettings] = useState<{enabled: boolean}>({enabled: false});
  const [signupSuccess, setSignupSuccess] = useState(false);

  useEffect(() => {
    fetchOAuthSettings();
    
    fetchOAuthSettings();
    
    fetchOAuthSettings();
    
    // Check if this is a password reset request
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    const token = params.get('token');

    if (type === 'recovery') {
      setIsForgotPassword(false);
      setIsSignUp(false);
      toast.info('Enter your new password below');
    } else if (type === 'developer' && token) {
      handleDeveloperAccess(token);
    }
  }, []);

  const fetchOAuthSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('oauth_settings')
        .select('enabled')
        .eq('provider', 'google')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching OAuth settings:', error);
        return;
      }

      if (data) {
        setOauthSettings(data);
      }
    } catch (error) {
      console.error('Error fetching OAuth settings:', error);
    }
  };

  const handleDeveloperAccess = async (token: string) => {
    try {
      const { data, error } = await supabase
        .rpc('verify_developer_access', { token_param: token });

      if (error) throw error;
      if (!data || data.length === 0) {
        toast.error('Invalid or expired access link');
        return;
      }

      const developer = data[0];
      setEmail(developer.email);
      setName(developer.name);
      
      if (!developer.password_changed) {
        toast.info('Please set your permanent password');
        setPassword(developer.temp_password);
      }
    } catch (error) {
      console.error('Error verifying developer access:', error);
      toast.error('Failed to verify developer access');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoadingGoogle(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth`
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      toast.error('Failed to sign in with Google');
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      // Request a password reset from Supabase
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?type=recovery`,
      });

      if (error) {
        throw error;
      }

      // Send custom email with reset link
      const resetLink = `${window.location.origin}/auth?type=recovery`;
      const emailSent = await sendPasswordResetEmail(email, encodeURIComponent(email));

      if (!emailSent) {
        throw new Error('Failed to send password reset email');
      }

      toast.success('Password reset instructions sent to your email');
      setIsForgotPassword(false);
    } catch (error) {
      console.error('Reset password error:', error);
      setError((error as Error).message);
      toast.error('Failed to send reset instructions');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode.trim() || !pendingUser) {
      toast.error('Please enter the verification code');
      return;
    }

    setVerifying2FA(true);
    try {
      const { data, error } = await supabase
        .rpc('verify_2fa_code', {
          user_id_param: pendingUser.id,
          code_param: verificationCode
        });

      if (error) throw error;

      if (data) {
        toast.success('Successfully verified');
        clearForm();
        setShowVerificationModal(false);
      } else {
        toast.error('Invalid or expired verification code');
      }
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      toast.error('Failed to verify code');
    } finally {
      setVerifying2FA(false);
    }
  };

  const clearForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setVerificationCode('');
    setPendingUser(null);
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setPendingUser(null);
    setError(null);

    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    const token = params.get('token'); // This will be the email for password reset
    
    let retries = 0;
    const maxRetries = 3;
    
    // Client-side validation
    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }
    
    // Validate password
    if (!isValidPassword(password)) {
      setError('Password must be at least 6 characters long and contain only valid characters');
      setLoading(false);
      return;
    }

    // Validate email format
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // For signup, validate name
    if (isSignUp && !name.trim()) {
      setError('Please enter your name');
      setLoading(false);
      return;
    }
    
    try {
      while (retries < maxRetries) {
        try {
          // Check if this is a password reset
          const params = new URLSearchParams(window.location.search);
          const type = params.get('type');
          const token = params.get('token');

          if (type === 'recovery') {
            try {
              // Verify the token (email) matches the entered email
              if (token && decodeURIComponent(token) === email) {
                // Update the user's password
                const { data, error: updateError } = await supabase.auth.updateUser({
                  password: password
                });

                if (updateError) throw updateError;

                toast.success('Password updated successfully');
                window.location.href = '/auth'; // Redirect to login
                return;
              } else {
                throw new Error('Invalid or expired password reset link');
              }
            } catch (error) {
              console.error('Password reset error:', error);
              throw error;
            }
          }

          if (isSignUp) {
            const { data, error: signUpError } = await supabase.auth.signUp({
              email,
              password,
              options: {
                data: { 
                  name,
                  email,
                },
              },
            });
            
            if (signUpError) {
              console.error('Signup error details:', signUpError);
              
              // Handle specific error cases
              if (signUpError.status === 422) {
                if (signUpError.message.includes('User already registered')) {
                throw new Error('An account with this email already exists');
                } else if (signUpError.message.includes('password')) {
                  throw new Error('Password error: ' + signUpError.message);
                } else if (signUpError.message.includes('email')) {
                  throw new Error('Email error: ' + signUpError.message);
                } else {
                  throw new Error('Validation error: ' + signUpError.message);
                }
              } 
              throw signUpError;
            }
            
            if (data?.user) {
              // Show success message
              setSignupSuccess(true);
              setTimeout(() => {
                setSignupSuccess(false);
              }, 10000); // Show for 10 seconds
              
              toast.success('Registration successful! You\'re logged in.');
              setIsSignUp(false);
              clearForm();
              window.location.href = '/overview';
              return;
            }
          } else {
            const { data, error: signInError } = await supabase.auth.signInWithPassword({
              email,
              password,
            });
            
            if (signInError) {
              if (signInError.status === 400) {
                setError('Invalid email or password. Please check your credentials.');
                return;
              } 
              throw signInError;
            }
            
            if (!data?.session || !data?.user) {
              throw new Error('Failed to sign in. Please try again.');
            }

            // Check if user has 2FA enabled
            const { data: profile } = await supabase
              .from('profiles')
              .select('two_factor_enabled')
              .eq('user_id', data.user.id)
              .single();

            if (profile?.two_factor_enabled) {
              // Generate and send 2FA code
              const { data: code, error: codeError } = await supabase
                .rpc('generate_2fa_code', { user_id_param: data.user.id });

              if (codeError) throw codeError;

              // Send verification code via email
              const emailSent = await send2FACode(data.user.email, code);
              if (!emailSent) {
                throw new Error('Failed to send verification code');
              }
              
              setPendingUser(data.user);
              setShowVerificationModal(true);
              return;
            }

            window.location.href = '/overview'; 
            return;
          }
        } catch (error) {
          if (isNetworkError(error) && retries < maxRetries - 1) {
            retries++;
            await new Promise(resolve => setTimeout(resolve, 1000 * retries));
            continue;
          }
          const errorMessage = error instanceof Error 
            ? error.message.includes('Invalid login credentials') || error.status === 400 
              ? 'Invalid email or password. Please check your credentials.'
              : error.message
            : 'An unexpected error occurred. Please try again.';
          
          setError(errorMessage);
          return;
        }
      }
      throw new Error('Unable to connect to the authentication service. Please check your internet connection.');
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'An unexpected error occurred. Please try again.';
      
      setError(errorMessage);
      if (errorMessage.includes('Unable to connect') || errorMessage.includes('Failed to create session')) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900/20 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-indigo-100/20 to-purple-100/20 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-full -mr-20 -mt-20 blur-2xl"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-purple-100/20 to-indigo-100/20 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-full -ml-20 -mb-20 blur-2xl"></div>
        
        <div className="text-center">
          <div className="flex justify-center mb-8">
            <Logo size="lg" />
          </div>
          <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
            Welcome to PM Pro AI. Your personalized PM tool.
          </p>
          {signupSuccess && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg">
              <p className="text-green-700 dark:text-green-300 font-medium">
                Thank you for signing up. Please confirm your email to sign in.
              </p>
            </div>
          )}
          
          <h2 className="text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400">
            {isForgotPassword 
              ? 'Reset your password'
              : isSignUp 
                ? 'Create your account' 
                : 'Sign in to your account'}
          </h2>
        </div>
        <form className="mt-10 space-y-6" onSubmit={isForgotPassword ? handleForgotPassword : handleAuth}>
          <div className="space-y-4">
            {isSignUp && !isForgotPassword && (
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Name *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required={isSignUp}
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg placeholder-gray-500 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 sm:text-sm"
                  placeholder="Enter your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            )}
            <div>
              <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Email address *
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg placeholder-gray-500 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 sm:text-sm"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {!isForgotPassword && <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Password *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg placeholder-gray-500 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 sm:text-sm"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>}
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              * Required fields
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/50 p-4 border border-red-200 dark:border-red-800">
              <div className="text-sm font-medium text-red-800 dark:text-red-200">{error}</div>
            </div>
          )}

          {!isForgotPassword && oauthSettings.enabled && (
            <div className="mt-6">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loadingGoogle}
                className="w-full flex justify-center py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loadingGoogle ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </>
                )}
              </button>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  {isForgotPassword 
                    ? 'Sending reset instructions...'
                    : isSignUp 
                      ? 'Creating account...' 
                      : 'Signing in...'}
                </div>
              ) : (
                isForgotPassword 
                  ? <>Send Reset Instructions</>
                  : isSignUp 
                    ? <><Mail className="h-5 w-5 mr-2" />Sign up with Email</> 
                    : <><LogIn className="h-5 w-5 mr-2" />Sign in with Email</>
              )}
            </button>
          </div>

          <div className="text-sm text-center space-y-4">
            <div className="space-y-2">
              {!isForgotPassword && (
                <button
                  type="button"
                  className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 transition-colors hover:underline"
                  onClick={() => {
                    setIsSignUp(!isSignUp);
                    setError(null);
                    setEmail('');
                    setPassword('');
                    setName('');
                  }}
                >
                  {isSignUp
                    ? 'Already have an account? Sign in'
                    : "Don't have an account? Sign up"}
                </button>
              )}
              {!isSignUp && !isForgotPassword && (
                <div>
                  <button
                    type="button"
                    className="font-medium text-gray-600 hover:text-gray-500 dark:text-gray-400 transition-colors hover:underline"
                    onClick={() => {
                      setIsForgotPassword(true);
                      setError(null);
                      setPassword('');
                    }}
                  >
                    Forgot your password?
                  </button>
                </div>
              )}
              {isForgotPassword && (
                <div>
                  <button
                    type="button"
                    className="font-medium text-gray-600 hover:text-gray-500 dark:text-gray-400 transition-colors hover:underline"
                    onClick={() => {
                      setIsForgotPassword(false);
                      setError(null);
                      setEmail('');
                    }}
                  >
                    Back to sign in
                  </button>
                </div>
              )}
            </div>
            <div className="mt-4">
              <a
                href="/"
                className="font-medium text-gray-600 hover:text-gray-500 dark:text-gray-400 transition-colors hover:underline"
              >
                ← Back to home
              </a>
            </div>
          </div>
        </form>
      </div>
      
      {/* 2FA Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Two-Factor Authentication Required
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Please enter the verification code sent to your email.
              </p>
              <input
                type="text"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter 6-digit code"
                className="block w-full px-3 py-3.5 border border-gray-300 dark:border-gray-600 rounded-lg placeholder-gray-500 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 sm:text-sm mb-6"
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowVerificationModal(false);
                    setVerificationCode('');
                    setPendingUser(null);
                  }}
                  className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerify2FA}
                  disabled={verifying2FA}
                  className="inline-flex items-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg shadow-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  {verifying2FA ? (
                    <>
                      <Loader2 className="animate-spin h-5 w-5 mr-2" />
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}